// src/utils/rulePlans/configRulePlans.js
// AWS Config and drift-state remediation plan helpers.

function pickConfigResource(item = {}, fallback = "the affected AWS resource") {
  return (
    item?.aws_context?.resourceId ||
    item?.aws_context?.resource_id ||
    item?.aws_context?.resourceName ||
    item?.aws_context?.resource_name ||
    item?.resource_id ||
    item?.resourceId ||
    fallback
  );
}

function pickConfigRuleName(item = {}, fallback = "the affected AWS Config rule") {
  return (
    item?.aws_context?.configRuleName ||
    item?.aws_context?.config_rule_name ||
    item?.finding_metadata?.configRuleName ||
    item?.finding_metadata?.config_rule_name ||
    item?.rule_name ||
    item?.ruleName ||
    fallback
  );
}

export function isConfigRule(ruleId = "") {
  const rule = String(ruleId || "").toUpperCase();

  return (
    rule.includes("AWS_CONFIG") ||
    rule.includes("CONFIG_") ||
    rule.includes("CONFIGRECORDER") ||
    rule.includes("CONFIG_RECORDER") ||
    rule.includes("DELIVERY_CHANNEL") ||
    rule.includes("RESOURCE_DRIFT") ||
    rule.includes("DRIFT_STATE") ||
    rule.includes("DRIFT_DETECTED") ||
    rule.includes("CONFIG_RULE")
  );
}

export function buildConfigFixPlanSteps(item = {}) {
  const rule = String(item?.rule_id || item?.ruleId || "").toUpperCase();
  const resource = pickConfigResource(item);
  const configRuleName = pickConfigRuleName(item);

  if (rule.includes("CONFIG_RECORDER") || rule.includes("CONFIGRECORDER") || rule.includes("RECORDER_DISABLED")) {
    return [
      "Open AWS Config.",
      "Go to Settings.",
      "Review the configuration recorder status.",
      "Enable recording for all required resource types in the account and region.",
      "Confirm the recorder uses the approved AWS Config service-linked role or configured IAM role.",
      "Confirm recording includes global resources where required by the governing control.",
      "Save changes and wait for AWS Config to begin recording.",
      "Run Full Cloud Assessment to verify AWS Config recorder evidence is refreshed.",
    ];
  }

  if (rule.includes("DELIVERY_CHANNEL") || rule.includes("SNAPSHOT_DELIVERY")) {
    return [
      "Open AWS Config.",
      "Go to Settings.",
      "Review the delivery channel configuration.",
      "Confirm the approved S3 delivery bucket is configured and accessible.",
      "Confirm SNS delivery is configured if required by the governing control.",
      "Repair bucket policy or service role permissions if AWS Config cannot deliver snapshots.",
      "Save changes and confirm a configuration snapshot can be delivered.",
      "Run Full Cloud Assessment to verify AWS Config delivery evidence is refreshed.",
    ];
  }

  if (rule.includes("CONFIG_RULE") || rule.includes("NONCOMPLIANT") || rule.includes("COMPLIANCE")) {
    return [
      "Open AWS Config.",
      "Go to Rules.",
      `Search for and open ${configRuleName}.`,
      "Review the noncompliant resource list and identify the resource referenced by this finding.",
      `Open ${resource} in the source AWS service.`,
      "Update the resource configuration to satisfy the AWS Config rule requirement.",
      "Re-evaluate the AWS Config rule or wait for automatic evaluation.",
      "Document any approved exception in OASYS.",
      "Run Full Cloud Assessment to verify the resource compliance state is refreshed.",
    ];
  }

  if (rule.includes("AGGREGATOR")) {
    return [
      "Open AWS Config.",
      "Go to Aggregators.",
      "Confirm the required organization or account aggregator exists.",
      "Review aggregator authorization, source accounts, and included regions.",
      "Repair missing account, region, or organization integration coverage.",
      "Confirm aggregated compliance data is updating.",
      "Document any approved exception in OASYS.",
      "Run Full Cloud Assessment to verify AWS Config aggregator evidence is refreshed.",
    ];
  }

  if (rule.includes("RESOURCE_DRIFT") || rule.includes("DRIFT_STATE") || rule.includes("DRIFT_DETECTED")) {
    return [
      "Open the AWS service that owns the affected resource.",
      `Search for and open ${resource}.`,
      "Compare the current resource configuration against the expected baseline in OASYS.",
      "Review AWS Config resource timeline or configuration history for when the drift began.",
      "Apply the approved baseline configuration or document an approved exception.",
      "Capture before/after evidence showing the configuration is back within policy.",
      "Return to OASYS and refresh drift state.",
      "Run Full Cloud Assessment to verify the drift state and posture are recalculated.",
    ];
  }

  return [
    "Open AWS Config.",
    "Review the recorder, delivery channel, rule, or resource timeline referenced by the finding.",
    `Investigate ${resource} and compare current configuration against the control requirement.`,
    "Apply the required AWS Config, source-resource, or drift remediation.",
    "Document any approved exception in OASYS.",
    "Run Full Cloud Assessment to verify AWS Config evidence and drift state are refreshed.",
  ];
}

export function buildConfigValidationText(item = {}) {
  const rule = String(item?.rule_id || item?.ruleId || "").toUpperCase();

  if (rule.includes("CONFIG_RECORDER") || rule.includes("CONFIGRECORDER") || rule.includes("RECORDER_DISABLED")) {
    return "OASYS will mark this improved after the next sync detects AWS Config recording is enabled for the required resources and regions.";
  }

  if (rule.includes("DELIVERY_CHANNEL") || rule.includes("SNAPSHOT_DELIVERY")) {
    return "OASYS will mark this improved after the next sync detects AWS Config snapshot delivery is configured and working.";
  }

  if (rule.includes("RESOURCE_DRIFT") || rule.includes("DRIFT_STATE") || rule.includes("DRIFT_DETECTED")) {
    return "OASYS will mark this improved after the next sync detects the resource configuration matches the expected baseline or the drift has an approved exception.";
  }

  return "OASYS will mark this improved after the next sync refreshes AWS Config evidence, resource compliance, and drift state.";
}
